// =========================================================================
// HelpyDoc · Toast — Sistema de notificaciones in-app
// Uso: toast.show("Mensaje", "success" | "error" | "info")
// =========================================================================
import { useState, useCallback, useRef } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ICONOS_ESTADO } from '@/config/iconografia';

export type ToastType = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

const COLORS: Record<ToastType, { bg: string; Icon: LucideIcon }> = {
  success: { bg: '#3A9E4A', Icon: ICONOS_ESTADO.exito },
  error:   { bg: '#F26B4A', Icon: ICONOS_ESTADO.advertencia },
  info:    { bg: '#2B8DD6', Icon: ICONOS_ESTADO.info },
};

export function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timerRefs = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const show = useCallback((message: string, type: ToastType = 'success', duration = 2500) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    timerRefs.current[id] = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      delete timerRefs.current[id];
    }, duration);
  }, []);

  return { toasts, show };
}

interface ToastContainerProps {
  toasts: ToastItem[];
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts }) => {
  if (toasts.length === 0) return null;
  return (
    <div style={{
      position: 'fixed', top: 56, left: '50%', transform: 'translateX(-50%)',
      zIndex: 99999, display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 8, pointerEvents: 'none',
      width: 'min(420px, 96vw)',
    }}>
      {toasts.map(t => {
        const { bg, Icon } = COLORS[t.type];
        return (
          <div key={t.id} style={{
            background: bg, color: '#fff', padding: '11px 18px',
            borderRadius: 999, fontSize: 14, fontWeight: 700,
            fontFamily: "'Hanken Grotesk', ui-sans-serif, sans-serif",
            boxShadow: '0 8px 24px rgba(0,0,0,.2)',
            display: 'flex', alignItems: 'center', gap: 8,
            animation: 'toastIn .25s cubic-bezier(.34,1.56,.64,1) both',
          }}>
            <Icon size={17} strokeWidth={2} style={{ flexShrink: 0 }} aria-hidden="true" /><span>{t.message}</span>
          </div>
        );
      })}
      <style>{`
        @keyframes toastIn { from { opacity:0; transform:translateY(-12px) scale(.9); } to { opacity:1; transform:none; } }
      `}</style>
    </div>
  );
};
